/**
 * Describes a virtual item (chat, contact, store button) that has been pinned
 * to a specific visual slot inside the combined ordered list.
 */
export interface PositionedVirtual<Item> {
  item: Item;
  /**
   * MULT-encoded position: slot * 10_000 + subIndex
   * where slot = number of real links that precede this virtual link.
   * Matches the encoding produced by splitCombinedReorder in linkReorder.ts.
   */
  position: number;
}

// Must stay in sync with MULT in linkReorder.ts.
const MULT = 10_000;

/**
 * Merges real link items with virtual (chatbot, contact form, store) items.
 *
 * Uses the same slot-based algorithm as buildCombinedItemIds so that the
 * editor and the live preview / public bio always render the same order:
 *
 * - Each virtual is inserted after exactly `slot` real items, where
 *   slot = floor(position / MULT).  Multiple virtuals at the same slot are
 *   ordered by ascending position (subIndex differentiates them).
 * - Virtuals whose slot exceeds realItems.length are clamped to the very end
 *   of the real-items list — they are never silently dropped.
 * - Trailing virtuals (passed via trailingVirtuals) are always appended last.
 *
 * `positionedVirtuals` is sorted internally — callers do not need to pre-sort.
 */
export function mergeWithPositionedVirtuals<Real, Virtual>(
  realItems: readonly Real[],
  positionedVirtuals: ReadonlyArray<PositionedVirtual<Virtual>>,
  trailingVirtuals: readonly Virtual[],
): Array<Real | Virtual> {
  if (positionedVirtuals.length === 0) {
    return [...realItems, ...trailingVirtuals];
  }

  // Sort by encoded position (slot first, then sub-index within slot).
  const sorted = [...positionedVirtuals].sort((a, b) => a.position - b.position);

  // Group virtuals by their slot, clamping out-of-range slots to realItems.length
  // so they appear just after the last real item rather than being dropped.
  const bySlot = new Map<number, Virtual[]>();
  for (const pv of sorted) {
    const slot = Math.min(
      Math.max(0, Math.floor(pv.position / MULT)),
      realItems.length,
    );
    if (!bySlot.has(slot)) bySlot.set(slot, []);
    bySlot.get(slot)!.push(pv.item);
  }

  const result: Array<Real | Virtual> = [];
  for (let i = 0; i <= realItems.length; i++) {
    const virtsHere = bySlot.get(i);
    if (virtsHere) result.push(...virtsHere);
    if (i < realItems.length) result.push(realItems[i]);
  }

  for (const item of trailingVirtuals) {
    result.push(item);
  }

  return result;
}
